<template>
    <div class="form-rich-text">
        <div
            v-if="editor"
            class="form-rich-text__toolbar"
        >
            <button>D</button>

            <button
                @click="editor.chain().focus().toggleBold().run()"
                :class="{ 'is-active': editor.isActive('bold') }"
                title="Bold"
            >
                <i class="fas fa-bold"></i>
            </button>
            <button
                @click="editor.chain().focus().toggleItalic().run()"
                :class="{ 'is-active': editor.isActive('italic') }"
                title="Italic"
            >
                <i class="fas fa-italic"></i>
            </button>
            <button
                @click="editor.chain().focus().toggleUnderline().run()"
                :class="{ 'is-active': editor.isActive('underline') }"
                title="Underline"
            >
                <i class="fas fa-underline"></i>
            </button>

            <div class="form-rich-text__toolbar__spacer" />

            <button
                @click="editor.chain().focus().toggleBulletList().run()"
                :class="{ 'is-active': editor.isActive('bulletList') }"
                title="Bulleted list"
            >
                <i class="fas fa-list"></i>
            </button>
            <button
                @click="editor.chain().focus().toggleOrderedList().run()"
                :class="{ 'is-active': editor.isActive('orderedList') }"
                title="Numbered list"
            >
                <i class="fas fa-list-ol"></i>
            </button>

            <div class="form-rich-text__toolbar__spacer" />

            <button
                @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
                :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
                title="Heading 2"
            >
                H2
            </button>
            <button
                @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
                :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
                title="Heading 3"
            >
                H3
            </button>
            <button
                @click="editor.chain().focus().toggleHeading({ level: 4 }).run()"
                :class="{ 'is-active': editor.isActive('heading', { level: 4 }) }"
                title="Heading 4"
            >
                H4
            </button>

            <div class="form-rich-text__toolbar__spacer" />

            <button
                @click="addLink"
                :class="{ 'is-active': editor.isActive('link') }"
                title="Add link"
            >
                <i class="fas fa-link"></i>
            </button>
            <button
                @click="editor.commands.unsetLink()"
                :class="{ 'is-active': editor.isActive('link') }"
                title="Remove link"
            >
                <i class="fas fa-unlink"></i>
            </button>

            <div class="form-rich-text__toolbar__spacer" />

            <button
                @click="viewSource"
                title="View source"
            >
                <i class="fas fa-code"></i>
            </button>
        </div>
        <div class="form-rich-text__content">
            <editor-content :editor="editor" />
        </div>

        <teleport to="body">
            <div
                v-if="toggleViewSource || toggleNewLink"
                class="form-rich-text__modal-blocker"
            />
            <div
                v-if="toggleViewSource"
                class="form-rich-text__modal form-rich-text__modal--view-source"
            >
                <div class="form-rich-text__modal__header">
                    <h2>View source</h2>
                    <a
                        class="form-rich-text__modal__header__close"
                        @click="closeViewSource"
                    >
                        <IconRenderer name="Cross"/>
                    </a>
                </div>
                <div class="form-rich-text__modal__details">
                    <FormTextarea
                        :model-value="viewSourceStr"
                        @update:modelValue="updateSourceModel"
                    />
                    <FormBtn
                        variant="beta"
                        @click="attachSource"
                    >
                        Update
                    </FormBtn>
                </div>
            </div>

            <div
                v-if="toggleNewLink"
                class="form-rich-text__modal form-rich-text__modal--add-link"
            >
                <div class="form-rich-text__modal__header">
                    <h2>Add link</h2>
                    <a
                        class="form-rich-text__modal__header__close"
                        @click="closeLink"
                    >
                        <IconRenderer name="Cross"/>
                    </a>
                </div>
                <div class="form-rich-text__modal__details">
                    <AdminAsset
                        :model-value="newLink"
                        @update:modelValue="updateModel"
                        :showView="false"
                        :showDelete="false"
                        :filesIcon="true"
                        class="resource-input"
                    />
                    <FormBtn
                        variant="beta"
                        @click="attachLink"
                    >
                        Add link
                    </FormBtn>
                </div>
            </div>
        </teleport>
    </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import FormTextarea from "./FormTextarea";

export default {
    components: {
        FormTextarea,
        EditorContent,
    },

    props: {
        modelValue: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            editor: null,
            newLink: null,
            toggleNewLink: false,
            toggleViewSource: false,
            viewSourceStr: null,
        }
    },

    watch: {
        modelValue(value) {
            const isSame = this.editor.getHTML() === value

            if (isSame) {
                return
            }

            this.editor.commands.setContent(value, false)
        },
    },

    methods: {
        updateModel(val){
            this.newLink = val;
        },

        updateSourceModel(val){
            this.viewSourceStr = val;
        },

        addLink() {
            this.newLink = this.editor.getAttributes('link').href;
            this.toggleNewLink = true;
        },

        closeLink() {
            this.toggleNewLink = false;
        },

        attachLink(){
            if(this.newLink.indexOf('http') > -1) {
                this.editor.commands.setLink({href: this.newLink, target: '_blank'});
            } else {
                this.editor.commands.setLink({href: this.newLink, target: '_self', rel: ''});
            }
            this.closeLink();
        },

        viewSource(){
            this.viewSourceStr = this.editor.getHTML();
            this.toggleViewSource = true;
        },

        closeViewSource(){
            this.toggleViewSource = false;
        },

        attachSource(){
            this.editor.commands.setContent(this.viewSourceStr, false)
            this.closeViewSource();
        }
    },

    mounted() {
        this.editor = new Editor({
            content: this.modelValue,
            extensions: [
                StarterKit,
                Link.configure({
                    openOnClick: false,
                }),
                Underline,
            ],
            onUpdate: () => {
                this.$emit('update:modelValue', this.editor.getHTML())
            },
        })
    },

    beforeUnmount() {
        this.editor.destroy()
    },
}
</script>

<style>
.form-rich-text {
    @apply flex flex-col relative border border-alto;

    &__toolbar {
        @apply border-b border-alto bg-white p-2 flex flex-row items-center justify-start font-sofia-pro filter drop-shadow;

        button {
            @apply border border-white text-cod-gray rounded-md flex flex-row items-center justify-center text-sm flex-shrink-0 mr-1 bg-white transition-colors duration-400 ease-in-out;
            width: 30px;
            height: 30px;

            &:first-child {
                @apply hidden;
            }

            &:hover {
                @apply bg-gallery border-gallery;
            }

            &.is-active {
                @apply bg-gallery border-gallery;
            }
        }

        &__spacer {
            @apply mx-2;
        }

    }

    &__content {
        @apply p-4 trim-content cursor-text h-full overflow-x-hidden overflow-y-auto;
        max-height: 50vh;

        .ProseMirror {
            @apply font-body trim-content;

            h1, h2, h3, h4, h5, h6 {
                @apply font-heading tracking-tight;
            }

            h1 {
                @apply text-6xl;
            }

            h2, h3, h4, h5 {
                @apply text-3xl;
            }

            h2, h3 {
                @apply mb-10;
            }

            p {
                @apply mb-5;
            }

            ul {
                li {
                    @apply mb-2;

                    &:last-child {
                        @apply mb-0;
                    }
                }
            }

            svg {
                @apply h-full;
                @apply max-w-full;
            }

            input, select {
                @apply text-lg;

                @screen lg {
                    @apply text-sm;
                }
            }

            hr {
                @apply my-6 p-0 text-grey-3;
            }

            b, strong {
                @apply font-bold;
            }
        }
    }

    &__modal-blocker {
        @apply fixed bg-cod-gray bg-opacity-60 w-screen h-screen top-0 left-0;
        z-index: 4000;
    }

    &__modal {
         @apply fixed flex flex-col bg-pampas top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 filter drop-shadow-lg;
         width: 80%;
         height: 80%;
         z-index: 4001;

        @screen md {
            width: 500px;
            height: 200px;
        }

        &--view-source {
            @screen md {
                width: 75%;
                height: 75%;
            }

            .form-textarea {
                @apply flex-grow mb-4;

                span, textarea {
                    @apply h-full;
                }

                textarea {
                    @apply text-black;
                    font-family: "Courier New", sans;
                    font-size: 14px;
                    line-height: 16px;
                    -webkit-font-smoothing: antialiased;
                    -moz-osx-font-smoothing: grayscale;
                }
            }

            .form-btn {
                @apply w-max;
            }
        }

        &__header {
            @apply flex flex-row justify-between items-center py-3 px-4 bg-cod-gray text-white;

            h2 {
                @apply text-lg m-0 p-0;
            }

            &__close {
                @apply cursor-pointer;

                svg {
                    @apply w-4;
                }
            }
        }

        &__details {
            @apply p-4 h-full flex flex-col flex-grow justify-between;
        }
    }
}
</style>
